import React, { useEffect, useState, useRef, useMemo } from 'react'
import { useWaldoEvent } from '@byscott-io/where-is-waldo'
import NewComment from './NewComment'
import Comment from './Comment'
import { withUserStore } from '../../../contexts/UserStore'

const CommentThread = ({ depth, isReply, thread, allComments: allCommentsFromParent, content, commentable, editing, buttons, children, onGetCommentButton, userStore }) => {
  const [showCommentThread, setShowCommentThread] = useState(false) // Start closed by default
  const [showNewComment, setShowNewComment] = useState(false)
  const [allComments, setAllComments] = useState([]) // All comments in the thread
  const [comments, setComments] = useState([]) // Filtered comments for this level
  const hasFetchedRef = useRef(false)

  useEffect(() => {
    if (Array.isArray(thread)) {
      setComments(thread)
      // Use allComments from parent if provided
      if (allCommentsFromParent) {
        setAllComments(allCommentsFromParent)
      }
    } else if (thread === null && commentable && !hasFetchedRef.current) {
      hasFetchedRef.current = true

      // Check if commentable already has comment_thread loaded
      if (commentable.commentThread && Array.isArray(commentable.commentThread)) {
        setAllComments(commentable.commentThread)
      } else {
        // Fetch comments from API
        userStore.getComments(commentable, null, (data) => {
          setAllComments(data.comments || [])
        })
      }
    }
  }, [thread, commentable, allCommentsFromParent, userStore])

  // Filter comments by the current commentable (for nested threading)
  useEffect(() => {
    if (allComments.length > 0 && commentable) {
      const filtered = allComments.filter(c =>
        c.commentableType === commentable.recordType &&
        c.commentableId === commentable.id
      )
      setComments(filtered)
    }
  }, [allComments, commentable])

  const saveNewComment = (content, cb) => {
    userStore.createComment(content, commentable, (comment) => {
      // Same-tab: optimistically add the new comment so the count updates immediately.
      if (comment) setAllComments((prev) => [...prev, comment])
      if (cb) cb(comment)
    })
  }

  // Cross-tab/session: refetch this thread when a relevant comment is created
  // elsewhere (#629, via the new useWaldoEvent hook). Only the top-level thread
  // that owns the fetch (thread === null) reacts, to avoid every nested instance refetching.
  useWaldoEvent('comment_created', (data) => {
    if (thread !== null || !commentable) return

    const matches = (type, id) => type === commentable.recordType && id === commentable.id
    if (matches(data.commentable_type, data.commentable_id) ||
        matches(data.root_commentable_type, data.root_commentable_id)) {
      userStore.getComments(commentable, null, (d) => setAllComments(d.comments || []))
    }
  })

  const setShowBothCommentAndThread = (newShowNewComment) => {
    if (newShowNewComment && !showCommentThread) { setShowCommentThread(true) }
    setShowNewComment(newShowNewComment)
  }

  // Single combined button for comments - memoized to prevent infinite loops
  const commentButton = useMemo(() => {
    if (editing) return null

    return (
      <span
        onClick={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (showCommentThread) {
            // Close thread and new comment form
            setShowCommentThread(false)
            setShowNewComment(false)
          } else {
            // Open thread and new comment form
            setShowBothCommentAndThread(true)
            setShowCommentThread(true)
          }
        }}
        style={{cursor: 'pointer', display: 'inline-block', color: '#0d6efd'}}
        title={comments && comments.length > 0 ? `${comments.length} comment${comments.length > 1 ? 's' : ''}` : (isReply ? 'Reply' : 'Add comment')}
      >
        <i className={`fas ${isReply ? 'fa-reply' : 'fa-comment'}`}></i>
        {comments && comments.length > 0 && (
          <span style={{ fontSize: '0.75em', marginLeft: '4px' }}>{comments.length}</span>
        )}
      </span>
    )
  }, [editing, comments.length, isReply, showCommentThread])

  // Call the callback to provide the button to parent
  useEffect(() => {
    if (onGetCommentButton) {
      onGetCommentButton(commentButton)
    }
  }, [onGetCommentButton, commentButton])

  return (<div>
    {!editing && content}
    {buttons}
    {children}
    {showCommentThread &&
      <div style={{ marginLeft: depth === 0 ? 0 : '8.33%' }}>
        {showNewComment &&
          <NewComment
            hideNewComment={() => setShowNewComment(false)}
            commentable={commentable}
            saveNewComment={saveNewComment}
          />}
        {comments?.map((t, i) => (
          <div key={i}> <Comment depth={depth + 1} comment={t} commentable={commentable} allComments={allComments} /> </div>
        ))}
      </div>}
  </div>)
}

export default withUserStore(CommentThread)
